import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adsApi } from '@/api/client';
import { parseAutoBiddingSettings, type AutoBiddingSettings } from '@/utils/autoBidding';
import type { EntityCrossFilter } from '@/utils/entityCrossFilter';
import type { WorkspacePageSize } from '@/hooks/useServerPagination';
import type { WorkspaceSortDir } from '@/components/workspace/useTableSort';
import type { MetricFilterPayload } from '@/utils/workspaceMetricFilters';

export type WorkspaceEntityMetrics = {
  impressions: number;
  clicks: number;
  cost: number;
  purchases30d: number;
  sales30d: number;
  ctr: number;
  cpc: number;
  acos: number;
  roas: number;
  [key: string]: number;
};

export type WorkspacePortfolioPageRow = {
  portfolioId: string;
  name: string;
  state: string;
  budget?: number | null;
  outOfBudgetBoostEnabled?: boolean;
  metrics: WorkspaceEntityMetrics;
};

export type WorkspaceCampaignPageRow = {
  campaignKey: string;
  campaignId: string;
  name: string;
  state: string;
  adProduct: string;
  portfolioId: string | null;
  autoBiddingEnabled?: boolean;
  autoBidding?: AutoBiddingSettings;
  outOfBudgetBoostEnabled?: boolean;
  metrics: WorkspaceEntityMetrics;
};

export function parseWorkspaceCampaignPageRow(row: WorkspaceCampaignPageRow): WorkspaceCampaignPageRow {
  if (row.autoBidding == null) return row;
  return { ...row, autoBidding: parseAutoBiddingSettings(row.autoBidding) };
}

export type WorkspaceAdGroupPageRow = {
  adGroupId: string;
  name: string;
  state: string;
  campaignId: string;
  campaignKey: string;
  adProduct: string;
  metrics: WorkspaceEntityMetrics;
};

export type WorkspaceKeywordPageRow = {
  keywordId: string;
  keywordText: string;
  matchType: string;
  state: string;
  adGroupId: string;
  campaignId: string;
  campaignKey: string;
  adProduct: string;
  bid?: number | null;
  rowKind?: 'keyword' | 'auto_target';
  metrics: WorkspaceEntityMetrics;
};

type EntityKind = 'portfolios' | 'campaigns' | 'ad-groups' | 'keywords';

const SEARCH_DEBOUNCE_MS = 300;

function parseEntityPage<T>(data: unknown): { rows: T[]; totalCount: number } {
  if (!data || typeof data !== 'object') return { rows: [], totalCount: 0 };
  const raw = data as Record<string, unknown>;
  const rows = Array.isArray(raw.rows) ? (raw.rows as T[]) : [];
  const totalCount = typeof raw.totalCount === 'number' ? raw.totalCount : rows.length;
  return { rows, totalCount };
}

function useWorkspaceEntityPageBase<T>({
  profileId,
  entity,
  startDate,
  endDate,
  page,
  pageSize,
  columnFilters,
  appliedFilter,
  sortBy,
  sortDir,
  metricFilters,
  requireActivity,
  enabled,
  mapRows,
}: {
  profileId: string;
  entity: EntityKind;
  startDate: string;
  endDate: string;
  page: number;
  pageSize: WorkspacePageSize;
  columnFilters: {
    search?: string;
    status?: string;
    adProduct?: string;
    matchType?: string;
    campaignStatus?: string;
  };
  appliedFilter: EntityCrossFilter;
  sortBy?: string;
  sortDir?: WorkspaceSortDir;
  metricFilters?: MetricFilterPayload[];
  /** False lists catalog campaigns that have no report rows in the date range. */
  requireActivity?: boolean;
  enabled: boolean;
  mapRows?: (row: T) => T;
}) {
  const offset = (page - 1) * pageSize;
  const searchValue = columnFilters.search ?? '';
  const [debouncedSearch, setDebouncedSearch] = useState(searchValue);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchValue), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchValue]);

  const requestColumnFilters = {
    ...columnFilters,
    search: debouncedSearch || undefined,
  };

  const query = useQuery({
    queryKey: [
      'workspace-entity-page',
      entity,
      profileId,
      startDate,
      endDate,
      pageSize,
      offset,
      debouncedSearch,
      requestColumnFilters.status ?? '',
      requestColumnFilters.adProduct ?? '',
      requestColumnFilters.matchType ?? '',
      requestColumnFilters.campaignStatus ?? '',
      sortBy ?? '',
      sortDir ?? 'asc',
      metricFilters ?? [],
      appliedFilter,
      requireActivity !== false,
    ],
    queryFn: async () => {
      const res = await adsApi.getWorkspaceEntityPage(profileId, entity, {
        limit: pageSize,
        offset,
        startDate,
        endDate,
        columnFilters: requestColumnFilters,
        appliedFilter: appliedFilter as unknown as Record<string, unknown>,
        sortBy,
        sortDir,
        metricFilters: metricFilters && metricFilters.length > 0 ? metricFilters : undefined,
        requireActivity: requireActivity === false ? false : undefined,
      });
      if (!res.success) throw new Error(`Failed to load workspace ${entity}`);
      const page = parseEntityPage<T>(res.data);
      return mapRows ? { ...page, rows: page.rows.map(mapRows) } : page;
    },
    enabled: Boolean(profileId) && enabled,
  });

  return {
    rows: query.data?.rows ?? [],
    totalCount: query.data?.totalCount ?? 0,
    isLoading: query.isLoading,
    isPending: query.isPending,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}

type SortArgs = {
  sortBy?: string;
  sortDir?: WorkspaceSortDir;
  metricFilters?: MetricFilterPayload[];
};

export function useWorkspacePortfoliosPage(args: {
  profileId: string;
  startDate: string;
  endDate: string;
  page: number;
  pageSize: WorkspacePageSize;
  search: string;
  status: string;
  appliedFilter: EntityCrossFilter;
  enabled: boolean;
} & SortArgs) {
  return useWorkspaceEntityPageBase<WorkspacePortfolioPageRow>({
    profileId: args.profileId,
    entity: 'portfolios',
    startDate: args.startDate,
    endDate: args.endDate,
    page: args.page,
    pageSize: args.pageSize,
    columnFilters: {
      search: args.search || undefined,
      status: args.status !== 'ALL' ? args.status : undefined,
    },
    appliedFilter: args.appliedFilter,
    sortBy: args.sortBy,
    sortDir: args.sortDir,
    metricFilters: args.metricFilters,
    enabled: args.enabled,
  });
}

export function useWorkspaceCampaignsPage(args: {
  profileId: string;
  startDate: string;
  endDate: string;
  page: number;
  pageSize: WorkspacePageSize;
  search: string;
  status: string;
  adProduct: string;
  appliedFilter: EntityCrossFilter;
  enabled: boolean;
  requireActivity?: boolean;
} & SortArgs) {
  return useWorkspaceEntityPageBase<WorkspaceCampaignPageRow>({
    profileId: args.profileId,
    entity: 'campaigns',
    startDate: args.startDate,
    endDate: args.endDate,
    page: args.page,
    pageSize: args.pageSize,
    columnFilters: {
      search: args.search || undefined,
      status: args.status !== 'ALL' ? args.status : undefined,
      adProduct: args.adProduct !== 'ALL' ? args.adProduct : undefined,
    },
    appliedFilter: args.appliedFilter,
    sortBy: args.sortBy,
    sortDir: args.sortDir,
    metricFilters: args.metricFilters,
    requireActivity: args.requireActivity,
    enabled: args.enabled,
    mapRows: parseWorkspaceCampaignPageRow,
  });
}

export function useWorkspaceAdGroupsPage(args: {
  profileId: string;
  startDate: string;
  endDate: string;
  page: number;
  pageSize: WorkspacePageSize;
  search: string;
  status: string;
  adProduct: string;
  campaignStatus: string;
  appliedFilter: EntityCrossFilter;
  enabled: boolean;
} & SortArgs) {
  return useWorkspaceEntityPageBase<WorkspaceAdGroupPageRow>({
    profileId: args.profileId,
    entity: 'ad-groups',
    startDate: args.startDate,
    endDate: args.endDate,
    page: args.page,
    pageSize: args.pageSize,
    columnFilters: {
      search: args.search || undefined,
      status: args.status !== 'ALL' ? args.status : undefined,
      adProduct: args.adProduct !== 'ALL' ? args.adProduct : undefined,
      campaignStatus: args.campaignStatus !== 'ALL' ? args.campaignStatus : undefined,
    },
    appliedFilter: args.appliedFilter,
    sortBy: args.sortBy,
    sortDir: args.sortDir,
    metricFilters: args.metricFilters,
    enabled: args.enabled,
  });
}

export function useWorkspaceKeywordsPage(args: {
  profileId: string;
  startDate: string;
  endDate: string;
  page: number;
  pageSize: WorkspacePageSize;
  search: string;
  status: string;
  matchType: string;
  appliedFilter: EntityCrossFilter;
  enabled: boolean;
} & SortArgs) {
  return useWorkspaceEntityPageBase<WorkspaceKeywordPageRow>({
    profileId: args.profileId,
    entity: 'keywords',
    startDate: args.startDate,
    endDate: args.endDate,
    page: args.page,
    pageSize: args.pageSize,
    columnFilters: {
      search: args.search || undefined,
      status: args.status !== 'ALL' ? args.status : undefined,
      matchType: args.matchType !== 'ALL' ? args.matchType : undefined,
    },
    appliedFilter: args.appliedFilter,
    sortBy: args.sortBy,
    sortDir: args.sortDir,
    metricFilters: args.metricFilters,
    enabled: args.enabled,
  });
}
